import { useEffect, useRef } from "react"
import { toast } from "react-toastify"

import { Badge } from "~/components/design-system/components"
import { SettingsRow, SettingsSection } from "~/components/design-system/pages"
import Select from "~/components/reusable/Select"
import { Skeleton } from "~/components/reusable/Skeleton"
import { UserAvatar } from "~/components/reusable/users/UserAvatar"
import { trackEvent } from "~/hooks/useTrackingService"
import { userRoleOptions } from "~/models/enum"
import { type UserRole, useUser } from "~/providers/userProvider"
import { type Auth0User } from "~/server/api/routers/auth0"
import { api } from "~/utils/api"
import { isProduction } from "~/utils/environment"
import { cn } from "~/utils/helpers"

import { RemoveUserDialog } from "./dialogs/RemoveUserDialog"
import { UserPopover } from "./UserPopover"

interface Props {
  organizationId?: string
  organizationName?: string
  className?: string
}

const LOADING_ROW_COUNT = 3

const getUserRoleOption = (user: Auth0User) =>
  userRoleOptions.find(
    (option) => option.value === user.roles.find((role) => role.name === option.value)?.name,
  ) ?? userRoleOptions.find((option) => option.value === "user")

const NonProductionNotice = () => (
  <span className="flex flex-wrap items-center gap-2">
    <Badge variant="yellow" size="sm">
      Not production
    </Badge>
    <span>
      You are currently not in a production environment. Changes to customer users are disabled.
    </span>
  </span>
)

export const MessageRow = ({ message }: { message: string }) => (
  <SettingsRow layout="stacked">
    <p className="paragraph-sm text-fg-subtle text-center">{message}</p>
  </SettingsRow>
)

export const UserManagementCard: React.FC<Props> = ({
  organizationId,
  organizationName,
  className,
}) => {
  const {
    data: users,
    isLoading: isLoadingUsers,
    error,
  } = api.auth0.getUsers.useQuery({
    organizationId,
    organizationName,
  })
  const utils = api.useUtils()
  const { permissions } = useUser()

  // Invalidate local user cache after auth0.getUsers reconciliation runs - This is ugly but unsure of any better approach
  const hasReconciled = useRef(false)
  useEffect(() => {
    if (users && !hasReconciled.current) {
      hasReconciled.current = true
      void utils.user.getOrgUsers.invalidate()
    }
  }, [users, utils])

  const updateUserRoles = api.auth0.updateUserRoles.useMutation({
    onError: async () => {
      toast.error("Failed to update user roles")
    },
    onSuccess: async () => {
      toast.success("User role has been updated")
      void utils.auth0.getUsers.invalidate({ organizationId })
      void utils.user.getOrgUsers.invalidate()
    },
  })

  function renderRows() {
    if (isLoadingUsers) {
      return Array.from({ length: LOADING_ROW_COUNT }, (_, index) => (
        <SettingsRow key={index} label={<Skeleton className="h-8 w-56" />}>
          <Skeleton className="h-8 w-32" />
        </SettingsRow>
      ))
    }
    if (error) return <MessageRow message={error.message} />
    if (!users?.length) return <MessageRow message="No users found" />

    return users.map((user) => (
      <SettingsRow
        key={user.user_id}
        density="compact"
        leading={
          <UserAvatar name={user.name} email={user.email} picture={user.picture} size={28} />
        }
        label={user.name}
        description={user.name !== user.email ? user.email : undefined}
      >
        <Select
          requires="administer"
          size="sm"
          wrapperClassName="w-[120px]"
          options={userRoleOptions}
          onChange={(option) => {
            trackEvent("User Role Updated", {
              Role: option!.value,
            })
            updateUserRoles.mutate({
              userId: user.user_id,
              role: option!.value as UserRole,
            })
          }}
          value={getUserRoleOption(user)}
        />
        {permissions.isStaff && <UserPopover user={user} />}
        <RemoveUserDialog user={user} organizationId={organizationId} />
      </SettingsRow>
    ))
  }

  const section = (
    <SettingsSection
      title="Members"
      description={isProduction() ? undefined : <NonProductionNotice />}
    >
      {renderRows()}
    </SettingsSection>
  )

  // Scroll wrapper only when a caller caps the height; otherwise it clips the card shadow.
  if (!className) return section
  return <div className={cn("min-h-0 overflow-y-auto", className)}>{section}</div>
}
